import { useEffect, useState } from 'react';

import { STORAGE_KEYS } from '@/constants/storageKeys';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { getPlatformStorage, safeStorageOp } from '@/util/platformStorage';

export type ContentLibraryGateStatus =
  | 'checking'
  | 'bypassed'
  | 'unattested'
  | 'attested'
  | 'declined';

const ATTESTED_VALUE = 'true';

const DECLINED_VALUE = 'false';

const SENTRY_TAGS = { component: 'useContentLibraryClinicianGate' };

/**
 * Determines whether an anonymous visitor may see full Content Library
 * content. Authenticated sessions bypass immediately (`lmp`/`expert` users
 * were already vetted as licensed prescribing clinicians at signup);
 * anonymous visitors are gated on a device-persisted attestation read via
 * `getPlatformStorage()`, since there is no account to check a backend
 * field against. Both a "yes" and a "no" answer persist on-device, so a
 * returning visitor is never re-asked either way — see `confirm()` and
 * `decline()`.
 */
export function useContentLibraryClinicianGate() {
  const { isAuthenticated } = useAuthStatus();

  const [storageStatus, setStorageStatus] = useState<
    'checking' | 'unattested' | 'attested' | 'declined'
  >('checking');

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    let isMounted = true;

    const readAttestation = async () => {
      const storage = getPlatformStorage();

      const value = await safeStorageOp(
        () => storage.getItem(STORAGE_KEYS.CONTENT_LIBRARY_CLINICIAN_ATTESTED),
        SENTRY_TAGS
      );

      if (!isMounted) {
        return;
      }

      if (value === ATTESTED_VALUE) {
        setStorageStatus('attested');
      } else if (value === DECLINED_VALUE) {
        setStorageStatus('declined');
      } else {
        setStorageStatus('unattested');
      }
    };

    void readAttestation();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  const confirm = async () => {
    setStorageStatus('attested');

    const storage = getPlatformStorage();

    await safeStorageOp(
      () => storage.setItem(STORAGE_KEYS.CONTENT_LIBRARY_CLINICIAN_ATTESTED, ATTESTED_VALUE),
      SENTRY_TAGS
    );
  };

  const decline = async () => {
    setStorageStatus('declined');

    const storage = getPlatformStorage();

    await safeStorageOp(
      () => storage.setItem(STORAGE_KEYS.CONTENT_LIBRARY_CLINICIAN_ATTESTED, DECLINED_VALUE),
      SENTRY_TAGS
    );
  };

  const status: ContentLibraryGateStatus = isAuthenticated ? 'bypassed' : storageStatus;

  return { status, confirm, decline };
}
